import { useState, useEffect, useCallback } from 'react';
import { apiGet, apiPost, apiPut, apiDelete } from '../../../api/axiosClient';

/**
 * Maps raw API template object to the UI-friendly shape.
 */
export const mapTemplate = (t) => ({
    id: t.template_id,
    name: t.name || t.client_name || 'Unnamed Theme',
    clientName: t.client_name || '',
    clientLogoUrl: t.logo_url || '',
    primaryColor: t.primary_color || '#2276E9',
    secondaryColor: t.secondary_color || '#1E293B',
    isCustom: true,
});

const BLANK_DRAFT = {
    name: '',
    clientName: '',
    clientLogoUrl: '',
    clientLogo: null,
    primaryColor: '#2276E9',
    secondaryColor: '#1E293B',
};

export const useThemes = () => {
    const [themes, setThemes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    const [draftData, setDraftData] = useState(BLANK_DRAFT);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formError, setFormError] = useState('');

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [themeToDelete, setThemeToDelete] = useState(null);
    const [toastMessage, setToastMessage] = useState('');

    const fetchThemes = useCallback(async () => {
        setIsLoading(true);
        setError('');
        try {
            const res = await apiGet('/templates');
            const data = res.data?.templates || [];
            setThemes(data.map(mapTemplate));
        } catch (err) {
            console.error('Failed to fetch themes:', err);
            setError('Failed to load themes.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchThemes();
    }, [fetchThemes]);

    const openCreate = () => {
        setDraftData(BLANK_DRAFT);
        setEditingId(null);
        setFormError('');
        setIsFormOpen(true);
    };

    const openEdit = (theme) => {
        setDraftData({
            name: theme.name,
            clientName: theme.clientName || '',
            clientLogoUrl: theme.clientLogoUrl || '',
            clientLogo: null,
            primaryColor: theme.primaryColor,
            secondaryColor: theme.secondaryColor,
        });
        setEditingId(theme.id);
        setFormError('');
        setIsFormOpen(true);
    };

    const closeForm = () => {
        setIsFormOpen(false);
        setEditingId(null);
        setDraftData(BLANK_DRAFT);
        setFormError('');
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (file.size > 2 * 1024 * 1024) { alert('File size exceeds 2MB limit'); return; }
        const reader = new FileReader();
        reader.onloadend = () => setDraftData(prev => ({ ...prev, clientLogo: file, clientLogoUrl: reader.result }));
        reader.readAsDataURL(file);
    };

    const handleRemoveLogo = () => setDraftData(prev => ({ ...prev, clientLogo: null, clientLogoUrl: '' }));

    const saveTheme = async () => {
        setFormError('');
        if (!draftData.name) { setFormError('Theme Name is required'); return; }
        try {
            const formData = new FormData();
            formData.append('name', draftData.name);
            if (draftData.clientName) formData.append('client_name', draftData.clientName);
            if (draftData.clientLogoUrl && !draftData.clientLogo) formData.append('logo_url', draftData.clientLogoUrl);
            formData.append('primary_color', draftData.primaryColor);
            formData.append('secondary_color', draftData.secondaryColor);
            if (draftData.clientLogo) formData.append('logo_file', draftData.clientLogo);

            if (editingId) {
                await apiPut(`/templates/${editingId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            } else {
                await apiPost('/templates', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            }
            await fetchThemes();
            closeForm();
            setToastMessage(editingId ? 'Theme updated!' : 'Theme created!');
            setTimeout(() => setToastMessage(''), 3000);
        } catch (err) {
            console.error('Failed to save theme:', err);
            const detail = err?.response?.data?.detail;
            if (Array.isArray(detail)) {
                setFormError(detail.map(d => d.msg).join('; '));
            } else {
                setFormError(typeof detail === 'string' ? detail : 'Failed to save theme. Please try again.');
            }
        }
    };

    const requestDelete = (theme) => {
        setThemeToDelete({ id: theme.id, title: theme.name || 'Unnamed Theme' });
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!themeToDelete) return false;
        try {
            await apiDelete(`/templates/${themeToDelete.id}`);
            await fetchThemes();
            setToastMessage('Theme deleted successfully');
            setTimeout(() => setToastMessage(''), 3000);
            setShowDeleteModal(false);
            setThemeToDelete(null);
            return true;
        } catch (err) {
            console.error('Failed to delete theme:', err);
            return false;
        }
    };

    return {
        themes, isLoading, error, fetchThemes,
        draftData, setDraftData, isFormOpen, editingId, formError,
        openCreate, openEdit, closeForm, handleFileChange, handleRemoveLogo, saveTheme,
        showDeleteModal, setShowDeleteModal, themeToDelete, requestDelete, confirmDelete,
        toastMessage,
    };
};
